import React from 'react';
import PropTypes from 'prop-types';
import { useSoyuzData, trans, getPagePath } from '@spotahome/soyuz/client';

import { APARTMENT, RESIDENCE, ROOM_TYPES } from '../../constants';
import cityToggleConfig from '../../config/cityToggleConfig';
import { useCityName } from '../../context/CitiesContext';

import SeoLandingLinks from './SeoLandingLinks';
import InternalLink from './InternalLink';
import TypedLinks from './TypedLinks';
import { ROOM_LINK_TITLE_CONFIG, ROOM_LINK_CONFIG } from './staticLinks';
import './InternalLinking.scss';
import getCityUrl from './getCityUrl';

const InternalLinkingWrapper = props => (
  <div data-test={props.dataTest}>
    <h3 className="internal-linking__title">{props.title}</h3>
    <div className="internal-linking__links--container">{props.children}</div>
  </div>
);

InternalLinkingWrapper.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  dataTest: PropTypes.string
};

InternalLinkingWrapper.defaultProps = {
  dataTest: null
};

const InternalLinking = ({ pageName, propertyTypes, className, cityId }) => {
  const cityName = useCityName(cityId);
  const isCoreCity = cityToggleConfig(cityId).isCoreCity;
  const { [pageName]: requestedData } = useSoyuzData();
  const { seoLinks = [] } = requestedData?.internalLinking || {};

  const hasSeoLinks = !!seoLinks?.length;
  const propertyTypeSelected = propertyTypes[0];
  const hasPropertyTypeSelected = propertyTypes.length > 0;
  const shouldShowLocationLinks = isCoreCity && propertyTypes.length === 1;

  const renderPropertyTypeLinks = () =>
    propertyTypeSelected === APARTMENT ? (
      <InternalLinkingWrapper
        title={trans('search.others.property_types')}
        dataTest="internal-property-type-links"
      >
        <TypedLinks types={ROOM_LINK_CONFIG('apartments')} areaSlug={cityId} />
      </InternalLinkingWrapper>
    ) : null;

  const renderSeoLinks = () =>
    (hasSeoLinks || shouldShowLocationLinks) && (
      <InternalLinkingWrapper
        dataTest="internal-seo-links"
        title={trans('search.others.users_search', { cityName })}
      >
        {shouldShowLocationLinks ? (
          <InternalLink
            text={trans(`city.for-rent.${propertyTypeSelected}`, {
              city: cityName
            })}
            url={getPagePath('externals.location_page', {
              params: {
                cityId,
                propertyType: propertyTypeSelected
              }
            })}
            hasComma={shouldShowLocationLinks && hasSeoLinks}
          />
        ) : null}
        <SeoLandingLinks links={seoLinks} />
      </InternalLinkingWrapper>
    );

  return (
    <div className={`internal-linking ${className}`}>
      {hasPropertyTypeSelected ? (
        <>
          {renderPropertyTypeLinks()}
          {propertyTypeSelected !== RESIDENCE ? renderSeoLinks() : null}
        </>
      ) : (
        <InternalLinkingWrapper
          title={trans('search.others.property_types')}
          dataTest="internal-property-type-links"
        >
          {ROOM_TYPES.map(roomType => (
            <>
              <InternalLink
                text={trans(
                  `search.others.${ROOM_LINK_TITLE_CONFIG(roomType).name}`
                )}
                url={getCityUrl(
                  cityId,
                  ROOM_LINK_TITLE_CONFIG(roomType).params
                )}
              />{' '}
            </>
          ))}
        </InternalLinkingWrapper>
      )}
    </div>
  );
};

InternalLinking.propTypes = {
  pageName: PropTypes.string.isRequired,
  className: PropTypes.string,
  propertyTypes: PropTypes.oneOf(ROOM_TYPES),
  cityId: PropTypes.string.isRequired
};

InternalLinking.defaultProps = {
  className: '',
  propertyTypes: []
};

export default InternalLinking;
